/**
 * Mixin for handling empty data states in data display pages
 * Provides computed properties and methods to manage:
 * - Whether filters have been applied
 * - Whether data is empty
 * - What message to display
 */

export const emptyStatesMixin = {
  data() {
    return {
      /**
       * Track if external filters (like Term, Year, Class, Stream) have been applied
       */
      filtersApplied: false,
      /**
       * Track if search filter is active
       */
      hasSearchFilter: false,
    };
  },
  computed: {
    /**
     * Determine the type of empty state message to show
     * Returns: 'empty', 'no-filter', 'no-results', 'no-search', or null
     */
    emptyStateType() {
      // If there's data, show nothing
      if (this.Items && this.Items.length > 0) {
        return null;
      }

      // If search filter is active and returned no results
      if (this.hasSearchFilter) {
        return "no-search";
      }

      // If external filters are applied and returned no results
      if (this.filtersApplied) {
        return "no-results";
      }

      // If no filters applied yet (for pages that require filters)
      if (this.requiresFilter) {
        return "no-filter";
      }

      // Default: completely empty
      return "empty";
    },

    /**
     * Whether to show the empty state message
     * Override 'requiresFilter' property in your component if needed
     */
    showEmptyState() {
      return this.Items && this.Items.length === 0;
    },

    /**
     * Determines if this page requires filters before showing data
     * Set to true in your component data if it requires filters
     */
    requiresFilter() {
      return this.$data.requiresFilter || false;
    },
  },
  methods: {
    /**
     * Call this when the user clicks Filter button to apply filters
     * @param {Object} filterParams - The filter parameters applied
     */
    markFiltersApplied(filterParams = {}) {
      this.filtersApplied = true;
      this.hasSearchFilter = false;
    },

    /**
     * Call this when the user clears filters
     */
    markFiltersClear() {
      this.filtersApplied = false;
      this.hasSearchFilter = false;
    },

    /**
     * Call this from the @filtered event to track search filtering
     * @param {Array} filteredItems - Items returned from the filter
     */
    onTableFiltered(filteredItems) {
      // Set search filter flag if user has typed in search box
      if (this.filter && this.filter.length > 0) {
        this.hasSearchFilter = true;
      } else {
        this.hasSearchFilter = false;
      }

      // Update the row count as usual
      if (this.Filtered) {
        this.Filtered(filteredItems);
      }
    },
  },
};
